<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">커뮤니티 광고 관리</h1>
          <p class="text-sm text-slate-500">파트너의 커뮤니티 광고 신청을 승인/거절합니다</p>
        </div>
      </div>

      <!-- 필터 탭 -->
      <div class="flex gap-2 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="currentTab = tab.value"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition',
            currentTab === tab.value
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
        >
          {{ tab.label }}
          <span v-if="tab.value === 'pending' && pendingCount > 0" class="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
            {{ pendingCount }}
          </span>
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="filteredAds.length === 0" class="bg-white rounded-2xl p-12 text-center">
        <svg class="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <h3 class="text-lg font-semibold text-slate-700 mb-2">
          {{ currentTab === 'pending' ? '대기 중인 신청이 없습니다' : '신청 내역이 없습니다' }}
        </h3>
      </div>

      <!-- 신청 목록 -->
      <div v-else class="space-y-4">
        <div
          v-for="ad in filteredAds"
          :key="ad.id"
          class="bg-white rounded-2xl overflow-hidden shadow-sm"
        >
          <!-- 광고 미리보기 (게시글 형태) -->
          <div class="border-b border-slate-100">
            <!-- 내용 -->
            <div class="p-4">
              <div class="flex items-center gap-2 mb-2">
                <span class="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded">AD</span>
                <span class="text-xs text-slate-400">{{ ad.propertyName }}</span>
                <!-- 상태 배지 -->
                <span :class="[
                  'ml-auto px-2 py-0.5 text-xs font-medium rounded-full',
                  getStatusClass(ad.status)
                ]">
                  {{ getStatusLabel(ad.status) }}
                </span>
              </div>
              <h3 class="font-semibold text-slate-800 mb-2">{{ ad.title }}</h3>
              <p class="text-sm text-slate-600 whitespace-pre-wrap">{{ ad.content }}</p>
            </div>
          </div>

          <!-- 광고 정보 -->
          <div class="p-4">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p class="text-slate-400">신청자</p>
                <p class="font-medium text-slate-700">{{ ad.partnerDisplayName }}</p>
              </div>
              <div>
                <p class="text-slate-400">현장</p>
                <p class="font-medium text-slate-700">{{ ad.propertyName }}</p>
              </div>
              <div>
                <p class="text-slate-400">캠페인 코드</p>
                <p class="font-medium text-slate-700">{{ ad.campaignCode }}</p>
              </div>
              <div>
                <p class="text-slate-400">노출 시간대</p>
                <p class="font-medium text-slate-700">
                  {{ ad.timeSlot?.label || '-' }}
                  <span v-if="ad.timeSlot" class="text-slate-500">({{ ad.timeSlot.start }}-{{ ad.timeSlot.end }})</span>
                </p>
              </div>
              <div>
                <p class="text-slate-400">신청일</p>
                <p class="font-medium text-slate-700">{{ formatDate(ad.createdAt) }}</p>
              </div>
              <div>
                <p class="text-slate-400">링크 URL</p>
                <p class="font-medium text-emerald-600 truncate">{{ ad.linkUrl }}</p>
              </div>
              <div v-if="ad.status === 'approved'">
                <p class="text-slate-400">노출/클릭</p>
                <p class="font-medium text-slate-700">{{ ad.impressions || 0 }} / {{ ad.clicks || 0 }}</p>
              </div>
            </div>

            <!-- 거절 사유 (거절된 경우) -->
            <div v-if="ad.status === 'rejected' && ad.rejectReason" class="mt-4 p-3 bg-red-50 rounded-xl">
              <p class="text-sm text-red-600">
                <strong>거절 사유:</strong> {{ ad.rejectReason }}
              </p>
            </div>

            <!-- 승인/거절 버튼 (대기 중인 경우) -->
            <div v-if="ad.status === 'pending'" class="mt-4 pt-4 border-t border-slate-100 flex gap-3">
              <button
                @click="handleApprove(ad)"
                :disabled="processing === ad.id"
                class="flex-1 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {{ processing === ad.id ? '처리 중...' : '승인' }}
              </button>
              <button
                @click="openRejectModal(ad)"
                :disabled="processing === ad.id"
                class="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                거절
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 거절 사유 모달 -->
      <div v-if="showRejectModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-2xl p-6 w-full max-w-md">
          <h3 class="text-lg font-semibold text-slate-800 mb-4">거절 사유 입력</h3>
          <textarea
            v-model="rejectReason"
            placeholder="거절 사유를 입력해주세요..."
            rows="4"
            class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none resize-none"
          ></textarea>
          <div class="flex gap-3 mt-4">
            <button
              @click="showRejectModal = false"
              class="flex-1 py-2.5 bg-slate-100 text-slate-600 font-medium rounded-lg hover:bg-slate-200 transition"
            >
              취소
            </button>
            <button
              @click="handleReject"
              :disabled="!rejectReason.trim() || processing"
              class="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              {{ processing ? '처리 중...' : '거절하기' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { useCommunityAd } from '@/composables/partners/useCommunityAd'

export default {
  name: 'CommunityAdApplicationsView',
  components: {
    AdminLayout
  },
  setup() {
    const { getAllCommunityAds, updateCommunityAdStatus } = useCommunityAd()

    const loading = ref(true)
    const communityAds = ref([])
    const currentTab = ref('pending')
    const processing = ref(null)
    const showRejectModal = ref(false)
    const rejectReason = ref('')
    const selectedAd = ref(null)

    const tabs = [
      { label: '대기', value: 'pending' },
      { label: '승인', value: 'approved' },
      { label: '거절', value: 'rejected' },
      { label: '전체', value: 'all' }
    ]

    const filteredAds = computed(() => {
      if (currentTab.value === 'all') return communityAds.value
      return communityAds.value.filter(ad => ad.status === currentTab.value)
    })

    const pendingCount = computed(() => {
      return communityAds.value.filter(ad => ad.status === 'pending').length
    })

    const loadCommunityAds = async () => {
      loading.value = true
      try {
        communityAds.value = await getAllCommunityAds()
      } catch (err) {
        console.error('광고 목록 로드 실패:', err)
      } finally {
        loading.value = false
      }
    }

    const getStatusLabel = (status) => {
      switch (status) {
        case 'pending': return '승인 대기'
        case 'approved': return '승인됨'
        case 'rejected': return '거절됨'
        default: return status
      }
    }

    const getStatusClass = (status) => {
      switch (status) {
        case 'pending': return 'bg-amber-100 text-amber-700'
        case 'approved': return 'bg-green-100 text-green-700'
        case 'rejected': return 'bg-red-100 text-red-600'
        default: return 'bg-slate-100 text-slate-600'
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    }

    const handleApprove = async (ad) => {
      if (!confirm(`${ad.partnerDisplayName}의 광고를 승인하시겠습니까?`)) return

      processing.value = ad.id
      try {
        await updateCommunityAdStatus(ad.id, 'approved')
        alert('승인되었습니다.')
        await loadCommunityAds()
      } catch (err) {
        console.error('승인 실패:', err)
        alert('승인에 실패했습니다: ' + err.message)
      } finally {
        processing.value = null
      }
    }

    const openRejectModal = (ad) => {
      selectedAd.value = ad
      rejectReason.value = ''
      showRejectModal.value = true
    }

    const handleReject = async () => {
      if (!selectedAd.value || !rejectReason.value.trim()) return

      processing.value = selectedAd.value.id
      try {
        await updateCommunityAdStatus(selectedAd.value.id, 'rejected', rejectReason.value.trim())
        alert('거절되었습니다.')
        showRejectModal.value = false
        await loadCommunityAds()
      } catch (err) {
        console.error('거절 실패:', err)
        alert('거절에 실패했습니다: ' + err.message)
      } finally {
        processing.value = null
      }
    }

    onMounted(() => {
      loadCommunityAds()
    })

    return {
      loading,
      communityAds,
      currentTab,
      tabs,
      filteredAds,
      pendingCount,
      processing,
      showRejectModal,
      rejectReason,
      getStatusLabel,
      getStatusClass,
      formatDate,
      handleApprove,
      openRejectModal,
      handleReject
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";
</style>
